import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from '@workspace/ui/components/sonner';
import { Spinner } from '@workspace/ui/components/spinner';
import { useCallback, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { m } from '~/paraglide/messages.js';
import { fetcher } from '~/utils/fetcher';
import { setLocalStorageItem } from '~/utils/local-storage';
import { useInviteProcessing } from './-hooks/use-invite-processing';
import type { Invite } from './-types/invite';

const ROUTES = {
  LOGIN: '/auth/login',
  EXTERNAL_INVITE: 'https://cuenta.bloque.app/#/invite-link/',
} as const;

const REDIRECT_DELAY = 2000;

export const Route = createFileRoute('/invite/')({
  validateSearch: (
    search: Record<string, string>,
  ): { code?: string; redirect?: string } => {
    return {
      code: search.code,
      redirect: search.redirect,
    };
  },
  component: InvitePage,
});

function InvitePage() {
  const navigate = useNavigate();
  const { code, redirect } = Route.useSearch();
  const { processInvite } = useInviteProcessing();

  const inviteCode = useMemo(() => code, [code]);

  const { data: invite, error } = useSWR<Invite>(
    () => (inviteCode ? `/invite/${inviteCode}` : null),
    fetcher,
  );

  const handleEmployeeRedirect = useCallback((code: string) => {
    toast.error(m['auth.invite.already_accepted']());
    const timeoutId = setTimeout(() => {
      window.location.href = `${ROUTES.EXTERNAL_INVITE}?code=${code}`;
    }, REDIRECT_DELAY);

    return () => clearTimeout(timeoutId);
  }, []);

  const navigateToLogin = useCallback(() => {
    navigate({ to: ROUTES.LOGIN });
  }, [navigate]);

  useEffect(() => {
    if (!inviteCode) return;

    if (error) {
      toast.error(m['auth.invite.not_found']());
      navigateToLogin();
      return;
    }

    if (invite?.status === 'accepted') {
      // Es un empleado, enviar a la app
      if (invite.details.roles.includes('employee')) {
        handleEmployeeRedirect(inviteCode);
        return;
      }

      // No es empleado, ir al login
      navigateToLogin();
      return;
    }

    if (invite) {
      processInvite(invite);
      return;
    }
  }, [invite, error]);

  useEffect(() => {
    if (redirect) {
      setLocalStorageItem('redirect', redirect);
    }
  }, []);

  return <Spinner />;
}
